// Generated by tutorials/record.cjs from tutorials/articles/h24-entering-weights.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h24 --text-only` (wording) or
// `node tutorials/record.cjs h24` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'entering-weights',
  catalogId: 'h24',
  title: 'Entering weights',
  summary: 'Enter a round of weights quickly on Weight Entry, and spot the residents whose weight changed enough to need a closer look.',
  audience: 'Dietary managers, dietitians and anyone who records residents\' weights',
  area: 'residents',
  learn: [
    'Find your way around Weight Entry',
    'Type a round of weights and save them together',
    'Read the percentage of change columns and their warning color',
    'Find weight alerts on the dashboard',
    'Fix, reweigh or delete a weight, and enter weekly weights',
    'Use one resident\'s Weight History',
    'Find where your facility sets the warning limits',
  ],
  keywords: [
    'weight',
    'weights',
    'weigh',
    'weight entry',
    'monthly weights',
    'weekly weights',
    'weight loss',
    'weight gain',
    'significant change',
    'percent change',
    'weight alerts',
    'reweigh',
    'fix a weight',
    'wrong weight',
    'delete weight',
    'weight history',
    'weekly entry',
    'entry mode',
    'percentage of change',
    'warning',
  ],
  steps: [
    {
      id: 'open',
      part: 'Enter the weights',
      heading: 'Open Weight Entry',
      text: 'In the menu, select Residents, then Weight Entry. Each row is a resident, and each column is a month, newest first. This month\'s column is empty until you enter its weights.',
      media: {
        image: {
          src: '/help/entering-weights/open.webp',
          width: 1280,
          height: 684,
        },
        alt: 'Weight Entry: residents with their unit, weight frequency and goal, and a column for each month, newest first, with this month\'s column still empty.',
      },
    },
    {
      id: 'toolbar',
      part: 'Enter the weights',
      heading: 'Check the mode and the date',
      text: 'Entry Mode switches between monthly and weekly columns. The next list shows all residents, or only those weighed weekly, monthly or on a tube feed.\n\nEntry Date is the date the weights are saved with. It is today unless you change it.',
      media: {
        image: {
          src: '/help/entering-weights/toolbar.webp',
          width: 1280,
          height: 72,
        },
        alt: 'The Weight Entry toolbar: Save, Entry Mode Monthly, All Residents, Monthly Display, and Entry Date today.',
      },
    },
    {
      id: 'type',
      part: 'Enter the weights',
      heading: 'Type the weights',
      text: 'Select the first resident\'s cell in this month\'s column and type the weight. Press Enter to move down to the next resident, and keep going.',
      media: {
        image: {
          src: '/help/entering-weights/type.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/type.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'This month\'s column with three new weights typed: 138.2, 204 and 130.8.',
      },
    },
    {
      id: 'alerts',
      part: 'Enter the weights',
      heading: 'Watch the percentage of change',
      text: 'The columns on the right show how much each weight changed over the last 1, 3 and 6 months. A change bigger than your facility\'s limit turns the cell yellow.\n\nHere Ines Carvalho has lost 6.4% in a month, so she needs a reweigh and a closer look.',
      media: {
        image: {
          src: '/help/entering-weights/alerts.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The row for Carvalho, Ines with -6.39 under 1M, -8.6 under 3M and -10.22 under 6M, all in yellow.',
      },
    },
    {
      id: 'save',
      part: 'Enter the weights',
      heading: 'Save',
      text: 'Select Save. Every weight you typed is saved at once, and becomes each resident\'s current weight.',
      media: {
        image: {
          src: '/help/entering-weights/save.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/save.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.4,
        },
        alt: 'Weight Entry after saving, with the new weights in this month\'s column and a message that the weights were saved.',
      },
    },
    {
      id: 'dashboard',
      part: 'Spot the alerts',
      heading: 'See the weight alerts on the dashboard',
      text: 'On the Dashboard, Weight Alerts lists residents with a significant change and those whose weight is overdue. Select a name to open the resident.',
      media: {
        image: {
          src: '/help/entering-weights/dashboard.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/dashboard.mp4',
          width: 1280,
          height: 684,
          durationSec: 4.3,
        },
        alt: 'The Weight Alerts card on the dashboard listing Carvalho, Ines and MacAllister, Douglas with a significant change, and Brennan, Walter overdue by 34 days.',
      },
    },
    {
      id: 'fix-typo',
      part: 'Fix or change a weight',
      heading: 'Fix a weight before you save',
      text: 'Typed the wrong number, such as 17.2 for 172? Before you save, select the cell again and type the right weight. Only the last weight you type in a cell is saved.\n\nA weight over 1000 is refused as you type it, so a slip such as 1720 never reaches the record.',
      media: {
        image: {
          src: '/help/entering-weights/fix-typo.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/fix-typo.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Weight Entry with 172 typed in this month\'s column for Ellison, Marjorie, and one unsaved change.',
      },
    },
    {
      id: 'fix-saved',
      part: 'Fix or change a weight',
      heading: 'Fix a weight you have saved',
      text: 'A weight saved earlier is fixed the same way: select its cell, type the right weight and save. When the Entry Date is the day that weight was entered on this screen, Dietech changes that weight instead of adding another.\n\nA weight typed with a different Entry Date is added as a new weight, which is how a reweigh on another day is kept. A small dot in the corner of a cell means that month has more than one weight. Point at the cell to see its date.',
      media: {
        image: {
          src: '/help/entering-weights/fix-saved.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/fix-saved.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Weight Entry after saving, with Achebe, Raymond\'s weight for this month changed from 204 to 203.4.',
      },
    },
    {
      id: 'delete-weight',
      part: 'Fix or change a weight',
      heading: 'Delete a weight',
      text: 'To delete a weight, select its cell, clear the number and save. If the month has another weight, that one shows in the cell instead.\n\nDeleting a weight needs the right role. If Dietech says you cannot, ask your administrator.',
      media: {
        image: {
          src: '/help/entering-weights/delete-weight.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/delete-weight.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Weight Entry after saving, with this month\'s cell for Adeyemi, Ruth empty again.',
      },
    },
    {
      id: 'weekly',
      part: 'Fix or change a weight',
      heading: 'Enter weekly weights',
      text: 'For residents weighed every week, set Entry Mode to Weekly. Each month then has a column for each week, W1 to W5, and the percentage of change columns count weeks instead of months. The week starts on the day your facility sets, shown at the bottom right. Pick Weekly Weight Residents in the next list to see only them.',
      media: {
        image: {
          src: '/help/entering-weights/weekly.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/weekly.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Weight Entry in Weekly mode: the newest months each split into weeks W1 to W5, with the weights saved today under this week of this month, percentage of change columns 1W, 2W and 4W, and Week starts: Monday at the bottom.',
      },
    },
    {
      id: 'history',
      part: 'One resident\'s weights',
      heading: 'Open the resident\'s Weight History',
      text: 'Open the resident and select Weight History on the left. Every weight is listed, newest first, with how far each is from the latest. Above the list are the height, the current BMI and the weight goal. A weight trigger, such as a 5% loss in 30 days, shows as a warning. Select Chart to see the weights as a graph.',
      media: {
        image: {
          src: '/help/entering-weights/history.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/history.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.9,
        },
        alt: 'The Weight History for Carvalho, Ines: a weight trigger warning for a 6.4% loss, and her weights from 113 lbs down to 107.9 lbs.',
      },
    },
    {
      id: 'history-new',
      part: 'One resident\'s weights',
      heading: 'Add a weight or a reweigh',
      text: 'Select New. Check the date, type the weight and add a note, such as Reweigh, so others know why there are two weights close together. Then select Save.',
      media: {
        image: {
          src: '/help/entering-weights/history-new.webp',
          width: 708,
          height: 578,
        },
        clip: {
          src: '/help/entering-weights/history-new.mp4',
          width: 708,
          height: 578,
          durationSec: 6,
        },
        alt: 'The Add Weight Entry window with today\'s date, a weight of 101.4 and the note Reweigh on the same scale.',
      },
    },
    {
      id: 'history-edit',
      part: 'One resident\'s weights',
      heading: 'Change a weight',
      text: 'To fix a weight or its date, double-click it in the list, or select it and then Open. Change it and select Save.',
      media: {
        image: {
          src: '/help/entering-weights/history-edit.webp',
          width: 708,
          height: 578,
        },
        clip: {
          src: '/help/entering-weights/history-edit.mp4',
          width: 708,
          height: 578,
          durationSec: 2,
        },
        alt: 'The Edit Weight Entry window for the weight of 107.9 lbs, with its date and weight.',
      },
    },
    {
      id: 'history-delete',
      part: 'One resident\'s weights',
      heading: 'Delete a weight',
      text: 'Select the weight, then Delete, and confirm. Dietech asks first, showing the weight and its date, because a deleted weight is gone from the history for good.',
      media: {
        image: {
          src: '/help/entering-weights/history-delete.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/history-delete.mp4',
          width: 1280,
          height: 684,
          durationSec: 3.4,
        },
        alt: 'The Delete Weight Entry confirmation asking to delete the weight entry of 107.9 lbs, with Cancel and Delete.',
      },
    },
    {
      id: 'warning-setting',
      part: 'Where the warning limit is set',
      heading: 'Your facility\'s limits',
      text: 'The yellow warning follows your facility settings. In the menu, select Setup, then Facility Settings, then Assessment and its Weight Entry tab.\n\nUnder Percentage of Change, the Monthly row sets the columns (1, 3 and 6 months here) and the Warning row below it the change that turns each one yellow: 5%, 7.5% and 10%. The Weekly rows do the same in weekly mode, and the color beside the last Warning is the warning color.\n\nChanging them needs the right to change facility settings, so ask your administrator.',
      media: {
        image: {
          src: '/help/entering-weights/warning-setting.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/entering-weights/warning-setting.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Facility Settings, Assessment, Weight Entry tab: the Percentage of Change grid with Weekly 1, 2 and 4 and warnings 2, 3 and 5, Monthly 1, 3 and 6 and warnings 5, 7.5 and 10, and the yellow warning color.',
      },
    },
  ],
  tips: [
    'Press Enter after each weight to move down to the next resident, so you can type a whole unit without the mouse.',
    'Press Ctrl and S to save.',
    'A change that looks wrong is often a scale or typing error. Reweigh before you act on it.',
  ],
  notes: [
    'The percentage of change on this screen compares the weights shown here. Weight trigger reports use their own rules, so their numbers can differ.',
    'The warning color and the 1M, 3M and 6M columns come from your facility settings. See the last part of this article.',
    'Weights you have not saved are lost if you leave the page. The count of unsaved changes is at the bottom right.',
  ],
  related: ['admit-resident', 'change-diet-order'],
  recorded: '2026-11-03',
};

export default article;
